import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LightPageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import ProgramApplicationStep from "../components/ProgramApplicationStep";
import StateRoutingScreen, { US_STATE_CODE_TO_NAME } from "../components/StateRoutingScreen";
import { 
  FileText, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  UserCheck,
  GraduationCap,
  CalendarDays,
  Clock,
  Lock,
  Mail,
  User,
  CreditCard,
  ShieldCheck,
  AlertTriangle,
  Save,
  Loader2,
  Paperclip,
  Upload
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";

const GUEST_STEPS = [
  { id: 1, title: "Intake Form", icon: FileText, description: "Basic information" },
  { id: 2, title: "Eligibility", icon: UserCheck, description: "Funding & eligibility" },
  { id: 3, title: "Program Readiness", icon: GraduationCap, description: "Readiness questions" },
  { id: 4, title: "Program of Interest", icon: CalendarDays, description: "Pick your program" },
  { id: 5, title: "Create Account", icon: Lock, description: "Save your application" }
];

const AUTH_STEPS = [
  { id: 1, title: "Intake Form", icon: FileText, description: "Basic information" },
  { id: 2, title: "Eligibility", icon: UserCheck, description: "Funding & eligibility" },
  { id: 3, title: "Program Readiness", icon: GraduationCap, description: "Readiness questions" },
  { id: 4, title: "Program of Interest", icon: CalendarDays, description: "Pick your program" }
];

const EnrollmentWizard = () => {
  const { user, setUser, registerWithPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isGuest = !user;
  const STEPS = isGuest ? GUEST_STEPS : AUTH_STEPS;
  const totalSteps = STEPS.length;

  const [currentStep, setCurrentStep] = useState(1);
  // After Step 1, we show a state-routing interstitial before continuing to Step 2.
  // If the applicant's state isn't on the open list, the screen offers a waitlist instead.
  const [showStateRouting, setShowStateRouting] = useState(false);
  // Guest-only: after the intake form we require account creation before continuing.
  const [showAccountGate, setShowAccountGate] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(!isGuest);
  const [submitting, setSubmitting] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [selectedCohortId, setSelectedCohortId] = useState("");

  // Guest step 5 state
  const [guestPassword, setGuestPassword] = useState("");
  const [guestConfirmPassword, setGuestConfirmPassword] = useState("");
  const [accountError, setAccountError] = useState("");

  // Guest cohort selection state (stored locally instead of submitting)
  const [guestCohortSelection, setGuestCohortSelection] = useState(null);

  // Payment state
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [stripeSessionId, setStripeSessionId] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // "Save & continue later" state (guest flow)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveEmail, setSaveEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);   // {resume_url, mocked_email}
  const [resumeFile, setResumeFile] = useState(null);  // required resume attachment (intake)

  // Silent autosave (guest flow only) — fires every 30s once we have an email
  const [autosaveState, setAutosaveState] = useState("idle"); // idle | saving | saved | error
  const [autosaveEmail, setAutosaveEmail] = useState("");
  
  // Form states
  const [intake, setIntake] = useState({
    first_name: "",
    middle_initial: "",
    last_name: "",
    email: user?.email || "",
    phone: "",
    date_of_birth: "",
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
    county: "",
    emergency_contact_name: "",
    emergency_contact_relation: "",
    emergency_contact_phone: "",
    how_heard: "",
    referral_source: ""
  });

  const [eligibility, setEligibility] = useState({
    citizenship_status: "",
    selective_service: null,
    employment_status: "",
    employer_name: "",
    job_title: "",
    hours_per_week: "",
    hourly_wage: "",
    has_benefits: false,
    is_dislocated_worker: false,
    separation_date: "",
    layoff_reason: "",
    previous_employer: "",
    receives_snap: false,
    receives_tanf: false,
    receives_ssi: false,
    receives_ssdi: false,
    receives_other_assistance: false,
    other_assistance_details: "",
    annual_family_income: "",
    family_size: "",
    is_veteran: false,
    is_veteran_spouse: false,
    is_foster_care: false,
    is_homeless: false,
    has_disability: true,
    disability_details: "",
    barriers_to_employment: "",
    gender: "",
    is_hispanic_latino: undefined,   // undefined = unanswered; true/false/null(prefer not) are valid answers
    race: [],
    is_english_primary: true,
    limited_english_proficiency: false,
    highest_education: "",
    is_currently_in_school: false,
    school_status: "",                  // "in_school" | "out_of_school"
    basic_skills_deficiency_tested: false,
    basic_skills_results: "",
    prior_certifications: "",
    accommodations_needed: ""
  });

  const [programApp, setProgramApp] = useState({
    interest_in_computers: "",
    tech_problem_solved: "",
    ideal_outcome: "",
    lamp_troubleshooting: "",
    best_learning_method: "",
    stuck_on_problem_reaction: "",
    classroom_difficulty: "",
    it_courses_taken: "",
    it_work_experience: "",
    tech_support_experience: "",
    accessibility_appeal: "",
    frustrating_website_experience: "",
    video_without_sound: "",
    green_button_problem: "",
    timer_accessibility_fix: "",
    keyboard_navigation: "",
    bug_report_choice: "",
    buttered_toast_steps: "",
    task_preference: "",
    spot_the_error: "",
    program_specific_answers: {},
    backup_program_id: "",          // Optional second-choice program (admin may consider)
    certification_agreed: false,
    consent_agreed: false,
    equal_opportunity_acknowledged: false,
    electronic_signature: "",
    signature_date: ""
  });

  useEffect(() => {
    fetchPrograms();
    if (!isGuest) loadEnrollment();
    else setLoading(false);
  }, [isGuest]);

  // ─── Deep-link to a specific step via ?step=N (preview/QA only) ──────────
  // Useful for previewing the eligibility/program/etc. screens directly without
  // having to fill out Step 1 first. Clamped to the valid step range.
  useEffect(() => {
    const stepParam = searchParams.get("step");
    if (!stepParam) return;
    const n = parseInt(stepParam, 10);
    if (Number.isNaN(n)) return;
    const clamped = Math.min(Math.max(n, 1), totalSteps);
    setCurrentStep(clamped);
  }, [searchParams, totalSteps]);

  // ─── Resume from "Save & continue later" link ─────────────────────────────
  // If the user lands here with `?resume=<token>`, fetch the saved draft and
  // rehydrate the wizard. Runs once on mount.
  useEffect(() => {
    const token = searchParams.get("resume");
    if (!token || !isGuest) return;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/enrollment-drafts/${token}`);
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          toast.error(d.detail || "This save link is no longer valid");
          return;
        }
        const d = await r.json();
        // Merge each section over the defaults so missing fields keep their defaults
        if (d.intake) setIntake((prev) => ({ ...prev, ...d.intake }));
        if (d.eligibility) setEligibility((prev) => ({ ...prev, ...d.eligibility }));
        if (d.program_application) setProgramApp((prev) => ({ ...prev, ...d.program_application }));
        if (d.cohort_selection?.program_id) setSelectedProgramId(d.cohort_selection.program_id);
        if (d.cohort_selection?.cohort_id) setSelectedCohortId(d.cohort_selection.cohort_id);
        if (d.cohort_selection?.is_waitlist !== undefined || d.cohort_selection?.cohort_id) {
          setGuestCohortSelection({
            program_id: d.cohort_selection.program_id,
            cohort_id: d.cohort_selection.cohort_id,
            is_waitlist: !!d.cohort_selection.is_waitlist,
          });
        }
        const step = Math.min(Math.max(parseInt(d.current_step || 1, 10), 1), 4);
        setCurrentStep(step);
        toast.success(`Welcome back! Picking up at step ${step} of your application.`);
      } catch (e) {
        toast.error("Couldn't load your saved application");
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Save & continue later — send a resume link by email ──────────────────
  const handleSaveDraft = async () => {
    const email = (saveEmail || intake.email || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address so we can send your resume link");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/enrollment-drafts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          intake,
          eligibility,
          program_application: programApp,
          cohort_selection: guestCohortSelection || {
            program_id: selectedProgramId || null,
            cohort_id: selectedCohortId || null,
          },
          current_step: currentStep,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Save failed");
      }
      const d = await r.json();
      setSaved(d);
      setAutosaveEmail(email);
      setAutosaveState("saved");
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ─── Silent autosave every 30s (guest flow) ───────────────────────────
  // Only kicks in once the guest has saved (or set an email and confirmed via dialog).
  // Quietly POSTs the latest snapshot — no toast, no dialog.
  useEffect(() => {
    if (!isGuest) return;
    if (!autosaveEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(autosaveEmail)) return;

    const id = setInterval(async () => {
      try {
        setAutosaveState("saving");
        const r = await fetch(`${API}/enrollment-drafts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: autosaveEmail,
            intake,
            eligibility,
            program_application: programApp,
            cohort_selection: guestCohortSelection || {
              program_id: selectedProgramId || null,
              cohort_id: selectedCohortId || null,
            },
            current_step: currentStep,
          }),
        });
        if (r.ok) {
          setAutosaveState("saved");
        } else {
          setAutosaveState("error");
        }
      } catch {
        setAutosaveState("error");
      }
    }, 30000);

    return () => clearInterval(id);
  }, [isGuest, autosaveEmail, intake, eligibility, programApp, guestCohortSelection, selectedProgramId, selectedCohortId, currentStep]);

  // Handle Stripe payment return
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");

    if (paymentStatus === "success" && sessionId) {
      setProcessingPayment(true);
      // Restore form data from localStorage (page reloaded after Stripe redirect)
      try {
        const savedIntake = localStorage.getItem("aspiritech_guest_intake");
        const savedEligibility = localStorage.getItem("aspiritech_guest_eligibility");
        const savedProgramApp = localStorage.getItem("aspiritech_guest_programapp");
        if (savedIntake) setIntake(JSON.parse(savedIntake));
        if (savedEligibility) setEligibility(JSON.parse(savedEligibility));
        if (savedProgramApp) setProgramApp(JSON.parse(savedProgramApp));
      } catch (e) { console.warn("Could not restore form data from localStorage", e); }

      // Verify payment
      const verifyPayment = async () => {
        try {
          const res = await fetch(`${API}/payments/status/${sessionId}`, { credentials: "include" });
          if (res.ok) {
            const data = await res.json();
            if (data.payment_status === "paid" || data.status === "complete") {
              setPaymentVerified(true);
              setStripeSessionId(sessionId);

              // Restore cohort selection from localStorage
              const savedCohort = localStorage.getItem("aspiritech_cohort_selection");
              if (savedCohort) {
                const cohortData = JSON.parse(savedCohort);
                setGuestCohortSelection({ ...cohortData, stripe_session_id: sessionId });
                setSelectedProgramId(cohortData.program_id || "");
              }

              toast.success("Payment confirmed! Complete your enrollment below.");

              if (isGuest) {
                setCurrentStep(5);
              } else {
                // For authenticated users, submit the enrollment now
                const savedSelection = localStorage.getItem("aspiritech_cohort_selection");
                if (savedSelection) {
                  const sel = JSON.parse(savedSelection);
                  await submitAuthCohortAfterPayment(sel.cohort_id, sessionId);
                }
              }
            } else {
              toast.error("Payment verification pending. Please try again.");
            }
          }
        } catch (err) {
          console.error("Payment verification error:", err);
          toast.error("Could not verify payment.");
        } finally {
          setProcessingPayment(false);
          // Clean up URL params
          setSearchParams({}, { replace: true });
        }
      };
      verifyPayment();
    } else if (paymentStatus === "cancelled") {
      // Restore form data on cancel too
      try {
        const savedIntake = localStorage.getItem("aspiritech_guest_intake");
        const savedEligibility = localStorage.getItem("aspiritech_guest_eligibility");
        const savedProgramApp = localStorage.getItem("aspiritech_guest_programapp");
        if (savedIntake) setIntake(JSON.parse(savedIntake));
        if (savedEligibility) setEligibility(JSON.parse(savedEligibility));
        if (savedProgramApp) setProgramApp(JSON.parse(savedProgramApp));
        const savedCohort = localStorage.getItem("aspiritech_cohort_selection");
        if (savedCohort) {
          const cohortData = JSON.parse(savedCohort);
          setSelectedProgramId(cohortData.program_id || "");
        }
      } catch (e) { console.warn("Could not restore form data", e); }
      toast.error("Payment was cancelled. You can try again.");
      setCurrentStep(4);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const submitAuthCohortAfterPayment = async (cohortId, sessionId) => {
    try {
      const res = await fetch(`${API}/enrollment/cohort-selection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ cohort_id: cohortId, stripe_session_id: sessionId })
      });
      if (res.ok) {
        toast.success("Enrollment submitted successfully!");
        localStorage.removeItem("aspiritech_cohort_selection");
        navigate("/dashboard");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to submit enrollment");
      }
    } catch { toast.error("An error occurred"); }
  };

  const fetchPrograms = async () => {
    try {
      const res = await fetch(`${API}/programs`);
      if (res.ok) setPrograms(await res.json());
    } catch (err) { console.error(err); }
  };

  const loadEnrollment = async () => {
    try {
      const response = await fetch(`${API}/enrollment/current`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setEnrollment(data);
          if (data.status === 'submitted') {
            setCurrentStep(5);
          } else if (data.status === 'application_complete') {
            setCurrentStep(4);
          } else if (data.status === 'eligibility_complete') {
            setCurrentStep(3);
          } else if (data.status === 'intake_complete') {
            setCurrentStep(2);
          } else {
            setCurrentStep(data.current_step || 1);
          }
          if (data.intake) setIntake(prev => ({ ...prev, ...data.intake }));
          if (data.eligibility) setEligibility(prev => ({ ...prev, ...data.eligibility }));
          if (data.program_application) setProgramApp(prev => ({ ...prev, ...data.program_application }));
          if (data.program_selection) {
            setSelectedProgramId(data.program_selection.program_id || "");
            setSelectedCohortId(data.program_selection.cohort_id || "");
          }
        }
      }
    } catch (error) {
      console.error("Error loading enrollment:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleIntakeChange = (field, value) => {
    setIntake(prev => ({ ...prev, [field]: value }));
  };

  const handleEligibilityChange = (field, value) => {
    setEligibility(prev => ({ ...prev, [field]: value }));
  };

  // Upload the required resume attachment (after the account exists / user is authenticated).
  const uploadResume = async () => {
    if (!resumeFile) return;
    const fd = new FormData();
    fd.append("file", resumeFile);
    try {
      const res = await fetch(`${API}/documents/upload?document_type=resume`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!res.ok) throw new Error("upload failed");
    } catch (e) {
      // Non-blocking (the account already exists), but tell the user so it isn't silently skipped.
      toast.error("We couldn't upload your resume. You can re-upload it from your dashboard once you're in.");
    }
  };

  const handleRaceChange = (race, checked) => {
    setEligibility(prev => {
      // "Prefer not to answer" is mutually exclusive with specific races.
      if (race === "prefer_not_to_answer") {
        return { ...prev, race: checked ? ["prefer_not_to_answer"] : [] };
      }
      const without = prev.race.filter(r => r !== race && r !== "prefer_not_to_answer");
      return { ...prev, race: checked ? [...without, race] : without };
    });
  };

  // ── Completion gates: every field on Intake & Eligibility is required ──────
  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());
  const s = (v) => !!(v && String(v).trim());

  const intakeChecks = (() => {
    const i = intake;
    const checks = [
      s(i.first_name), s(i.last_name), isValidEmail(i.email), s(i.phone), s(i.date_of_birth),
      s(i.street_address), s(i.city), s(i.state), s(i.zip_code), s(i.county),
      s(i.emergency_contact_name), s(i.emergency_contact_relation), s(i.emergency_contact_phone),
      s(i.how_heard), !!resumeFile,
    ];
    if (i.how_heard === "referral") checks.push(s(i.referral_source));
    return checks;
  })();
  const intakeRemaining = intakeChecks.filter((c) => !c).length;
  const intakeComplete = intakeRemaining === 0;

  const eligibilityChecks = (() => {
    const e = eligibility;
    const checks = [
      !!e.citizenship_status, !!e.employment_status, s(e.barriers_to_employment), !!e.gender,
      e.is_hispanic_latino !== undefined, (e.race && e.race.length > 0),
      !!e.highest_education, !!e.school_status, s(e.accommodations_needed),
    ];
    if (e.employment_status === "employed" || e.employment_status === "underemployed") {
      checks.push(s(e.employer_name), s(e.job_title), s(e.hours_per_week), s(e.hourly_wage));
    }
    if (e.employment_status === "dislocated_worker") {
      checks.push(s(e.previous_employer), s(e.separation_date), s(e.layoff_reason));
    }
    return checks;
  })();
  const eligibilityRemaining = eligibilityChecks.filter((c) => !c).length;
  const eligibilityComplete = eligibilityRemaining === 0;

  // For authenticated users: submit step to backend
  const submitStepAuth = async (step) => {
    setSubmitting(true);
    try {
      let endpoint, data;
      switch (step) {
        case 1:
          endpoint = "/enrollment/intake";
          data = intake;
          break;
        case 2:
          endpoint = "/enrollment/eligibility";
          data = {
            ...eligibility,
            hours_per_week: eligibility.hours_per_week ? parseInt(eligibility.hours_per_week) : null,
            hourly_wage: eligibility.hourly_wage ? parseFloat(eligibility.hourly_wage) : null,
            annual_family_income: eligibility.annual_family_income ? parseFloat(eligibility.annual_family_income) : null,
            family_size: eligibility.family_size ? parseInt(eligibility.family_size) : null
          };
          break;
        default:
          return;
      }

      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        setEnrollment(result);
        if (step === 1) {
          await uploadResume();
          // Show state-routing interstitial after Intake instead of jumping straight to Step 2.
          setShowStateRouting(true);
        } else {
          setCurrentStep(step + 1);
        }
        toast.success(`Step ${step} completed!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  // For guest users: just advance step (data stays in state)
  const submitStepGuest = (step) => {
    if (step === 1) {
      // After Intake, REQUIRE the guest to create an account before continuing.
      // The state-routing interstitial runs afterwards (now as an authenticated user).
      setShowAccountGate(true);
      toast.success("Step 1 completed!");
      return;
    }
    setCurrentStep(step + 1);
    toast.success(`Step ${step} completed!`);
  };

  const submitStep = (step) => {
    if (isGuest) {
      submitStepGuest(step);
    } else {
      submitStepAuth(step);
    }
  };

  // Step 3: Submit program + application questions
  const handleProgramApplicationSubmit = async (formData) => {
    if (isGuest) {
      // For guests, store form data locally and advance
      setProgramApp(prev => ({ ...prev, ...formData }));
      setCurrentStep(4);
      toast.success("Application saved! Now choose your start date.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/enrollment/program-application`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        const result = await res.json();
        setEnrollment(result);
        setCurrentStep(4);
        toast.success("Application saved! Now choose your start date.");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to submit application");
      }
    } catch { toast.error("An error occurred"); }
    finally { setSubmitting(false); }
  };

  // Step 4: Submit cohort date selection
  const handleCohortSelection = async (cohortId, isWaitlist = false) => {
    if (isWaitlist) {
      // Waitlist — NO payment required
      if (isGuest) {
        setGuestCohortSelection({ cohort_id: cohortId, waitlist: true });
        setCurrentStep(5);
        toast.success("You've selected the waitlist. Now create your account to save your application!");
      } else {
        setSubmitting(true);
        try {
          const res = await fetch(`${API}/enrollment/cohort-selection`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ cohort_id: cohortId, waitlist: true })
          });
          if (res.ok) {
            toast.success("You've been added to the waitlist! We'll be in touch.");
            navigate("/dashboard");
          } else {
            const err = await res.json();
            toast.error(err.detail || "Failed to submit");
          }
        } catch { toast.error("An error occurred"); }
        finally { setSubmitting(false); }
      }
      return;
    }

    // Non-waitlist: Payment required — redirect to Stripe Checkout
    setSubmitting(true);
    try {
      const programName = programs.find(p => p.program_id === selectedProgramId)?.name || "";

      // Save cohort selection to localStorage for after payment return
      localStorage.setItem("aspiritech_cohort_selection", JSON.stringify({
        cohort_id: cohortId,
        program_id: selectedProgramId,
        program_name: programName,
      }));

      // Also save form data for guest flow
      if (isGuest) {
        localStorage.setItem("aspiritech_guest_intake", JSON.stringify(intake));
        localStorage.setItem("aspiritech_guest_eligibility", JSON.stringify(eligibility));
        localStorage.setItem("aspiritech_guest_programapp", JSON.stringify(programApp));
      }

      const originUrl = window.location.origin;
      const res = await fetch(`${API}/payments/create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          origin_url: originUrl,
          enrollment_context: {
            program_id: selectedProgramId,
            program_name: programName,
            cohort_id: cohortId,
            email: intake.email || user?.email || "",
          }
        })
      });

      if (res.ok) {
        const data = await res.json();
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to initiate payment");
      }
    } catch {
      toast.error("An error occurred creating payment session");
    } finally {
      setSubmitting(false);
    }
  };

  // Guest-only: create the account immediately after the intake form, then
  // continue the remaining steps as an authenticated user (intake carries over).
  const handleEarlyAccountCreation = async (e) => {
    e.preventDefault();
    setAccountError("");
    if (guestPassword.length < 6) {
      setAccountError("Password must be at least 6 characters");
      return;
    }
    if (guestPassword !== guestConfirmPassword) {
      setAccountError("Passwords do not match");
      return;
    }
    const email = (intake.email || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAccountError("A valid email is required. Please go back and check your intake form.");
      return;
    }
    setSubmitting(true);
    try {
      const fullName = `${intake.first_name || ""} ${intake.last_name || ""}`.trim() || email;
      // 1) Create the account — this logs the user in and sets the session cookie.
      await registerWithPassword(email, guestPassword, fullName);
      // 2) Persist the intake data to the brand-new account.
      const res = await fetch(`${API}/enrollment/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(intake),
      });
      if (res.ok) setEnrollment(await res.json());
      // Upload the required resume now that the account exists.
      await uploadResume();
      // 3) Clean up guest drafts and move into the (now authenticated) routing step.
      localStorage.removeItem("aspiritech_guest_intake");
      localStorage.removeItem("aspiritech_guest_eligibility");
      localStorage.removeItem("aspiritech_guest_programapp");
      setGuestPassword("");
      setGuestConfirmPassword("");
      setShowAccountGate(false);
      setShowStateRouting(true);
      toast.success("Account created! Let's finish your application.");
    } catch (err) {
      setAccountError(err?.message || "Could not create your account. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Step 5 (Guest only): Create account + submit all enrollment data
  const handleGuestAccountCreation = async (e) => {
    e.preventDefault();
    setAccountError("");

    if (guestPassword.length < 6) {
      setAccountError("Password must be at least 6 characters");
      return;
    }
    if (guestPassword !== guestConfirmPassword) {
      setAccountError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      // Restore form data from localStorage if returning from Stripe
      const savedIntake = localStorage.getItem("aspiritech_guest_intake");
      const savedEligibility = localStorage.getItem("aspiritech_guest_eligibility");
      const savedProgramApp = localStorage.getItem("aspiritech_guest_programapp");

      const finalIntake = savedIntake ? JSON.parse(savedIntake) : intake;
      const finalEligibility = savedEligibility ? JSON.parse(savedEligibility) : eligibility;
      const finalProgramApp = savedProgramApp ? JSON.parse(savedProgramApp) : programApp;

      const fullName = `${finalIntake.first_name} ${finalIntake.last_name}`.trim();
      const eligibilityData = {
        ...finalEligibility,
        hours_per_week: finalEligibility.hours_per_week ? parseInt(finalEligibility.hours_per_week) : null,
        hourly_wage: finalEligibility.hourly_wage ? parseFloat(finalEligibility.hourly_wage) : null,
        annual_family_income: finalEligibility.annual_family_income ? parseFloat(finalEligibility.annual_family_income) : null,
        family_size: finalEligibility.family_size ? parseInt(finalEligibility.family_size) : null
      };

      const cohortSel = guestCohortSelection || { waitlist: true };

      const res = await fetch(`${API}/guest/enrollment/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: finalIntake.email,
          password: guestPassword,
          name: fullName,
          intake: finalIntake,
          eligibility: eligibilityData,
          program_application: {
            ...finalProgramApp,
            program_id: cohortSel.program_id || selectedProgramId,
            program_name: cohortSel.program_name || programs.find(p => p.program_id === selectedProgramId)?.name || ""
          },
          cohort_selection: cohortSel,
          stripe_session_id: stripeSessionId || cohortSel.stripe_session_id || null,
        })
      });

      if (res.ok) {
        const result = await res.json();
        setUser(result.user);
        // Clean up localStorage
        localStorage.removeItem("aspiritech_cohort_selection");
        localStorage.removeItem("aspiritech_guest_intake");
        localStorage.removeItem("aspiritech_guest_eligibility");
        localStorage.removeItem("aspiritech_guest_programapp");
        toast.success("Account created and enrollment submitted!");
        navigate("/dashboard", { replace: true });
      } else {
        const err = await res.json();
        setAccountError(
          typeof err.detail === 'string' ? err.detail :
          Array.isArray(err.detail) ? err.detail.map(e => e.msg || JSON.stringify(e)).join(' ') :
          "Something went wrong. Please try again."
        );
      }
    } catch (err) {
      setAccountError("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedProgram = programs.find(p => p.program_id === selectedProgramId);

  const formatCohortDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  };

  const progressPercentage = ((currentStep - 1) / totalSteps) * 100;

  if (loading || processingPayment) {
    return (
      <LightPageLayout title="Enrollment">
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-8 h-8 border-4 border-[#FFD507] border-t-transparent rounded-full animate-spin" />
          {processingPayment && <p className="text-sm text-slate-500">Verifying your payment...</p>}
        </div>
      </LightPageLayout>
    );
  }

  return (
    <LightPageLayout 
      title="Academy Enrollment"
      subtitle="Complete the enrollment process to join Aspiritech Academy."
    >
      <div className="max-w-4xl mx-auto space-y-8" data-testid="enrollment-wizard">
        {/* Progress Header */}
        <Card className="card-light">
          <CardContent className="py-6">
            <div className="flex items-center justify-between mb-4">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isComplete = currentStep > step.id;
                
                return (
                  <div key={step.id} className="flex items-center flex-1 min-w-0 last:flex-none">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`
                        w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0
                        ${isComplete ? 'bg-[#10B981] text-white' : 
                          isActive ? 'bg-[#FFD507] text-white' : 
                          'bg-slate-100 text-slate-400 border border-slate-200'}
                      `}>
                        {isComplete ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                      </div>
                      <div className="hidden md:block min-w-0">
                        <p className={`font-medium text-xs leading-tight truncate ${isActive ? 'text-[#0F1B3D]' : 'text-slate-500'}`}>
                          {step.title}
                        </p>
                        <p className="text-[10px] text-slate-400 truncate">{step.description}</p>
                      </div>
                    </div>
                    {index < STEPS.length - 1 && (
                      <div className={`flex-shrink-0 w-6 lg:w-10 h-0.5 mx-1.5 lg:mx-3 ${currentStep > step.id ? 'bg-[#10B981]' : 'bg-slate-200'}`} />
                    )}
                  </div>
                );
              })}
            </div>
            <Progress value={progressPercentage} className="h-2 bg-slate-100" />
          </CardContent>
        </Card>

        {/* Guest mode info banner — bigger, more prominent Save action */}
        {isGuest && currentStep <= 4 && (
          <div className="bg-gradient-to-r from-amber-50 to-blue-50 border-2 border-amber-200 rounded-xl px-5 py-4 flex items-center gap-4 flex-wrap shadow-sm" data-testid="guest-mode-banner">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Save className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <p className="text-sm font-semibold text-slate-900">
                You can create an account at any time to save your application.
              </p>
              <p className="text-xs text-slate-600 mt-0.5" data-testid="autosave-indicator">
                {autosaveState === "saving"
                  ? "Saving your progress…"
                  : autosaveState === "saved"
                    ? `Auto-saved · resume link emailed to ${autosaveEmail || "your email"}`
                    : "Or save with a one-click resume link emailed to you (no password needed)."}
              </p>
            </div>
            <Button
              size="lg"
              onClick={() => { setSaveEmail(intake.email || ""); setSaved(null); setSaveDialogOpen(true); }}
              className="bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-md"
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" /> Save &amp; Continue Later
            </Button>
          </div>
        )}

        {/* Step 1: Intake Form */}
        {currentStep === 1 && !showStateRouting && !showAccountGate && (
          <>
          <div className="relative rounded-xl overflow-hidden h-40 sm:h-48" data-testid="enrollment-banner">
            <img
              src="https://aspiritech.org/wp-content/uploads/2025/09/Aspiritech-1091-1024x683.jpg"
              alt="Aspiritech Academy"
              className="w-full h-full object-cover"
            />
            {/* Stronger left-side overlay so the title + subtitle always sit on a dark navy background */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F1B3D]/95 via-[#0F1B3D]/80 to-[#0F1B3D]/45 flex items-center px-8">
              <div>
                <p className="text-[#FFD507] text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-2 drop-shadow-md">Welcome to</p>
                <h2 className="text-white text-2xl sm:text-3xl font-heading font-bold drop-shadow-md">Aspiritech Academy Enrollment</h2>
                <p className="text-white text-sm sm:text-base mt-2 max-w-md leading-relaxed drop-shadow-md">Grant-funded tech career training and soft skills development for neurodivergent learners.</p>
              </div>
            </div>
          </div>

          <Card className="card-light" data-testid="step-1-intake">
            <CardHeader>
              <CardTitle className="text-[#0F1B3D] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1E3A8A]" />
                Step 1: Intake Form
              </CardTitle>
              <CardDescription className="text-slate-500">
                Provide your basic information to get started
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">First Name *</Label>
                    <Input value={intake.first_name} onChange={(e) => handleIntakeChange('first_name', e.target.value)} className="input-light" data-testid="input-first-name" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Middle Initial</Label>
                    <Input value={intake.middle_initial} onChange={(e) => handleIntakeChange('middle_initial', e.target.value)} className="input-light" maxLength={1} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Last Name *</Label>
                    <Input value={intake.last_name} onChange={(e) => handleIntakeChange('last_name', e.target.value)} className="input-light" data-testid="input-last-name" required />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Email *</Label>
                    <Input type="email" value={intake.email} onChange={(e) => handleIntakeChange('email', e.target.value)} className="input-light" data-testid="input-email" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Phone *</Label>
                    <Input type="tel" value={intake.phone} onChange={(e) => handleIntakeChange('phone', e.target.value)} className="input-light" placeholder="(555) 123-4567" data-testid="input-phone" required />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Date of Birth *</Label>
                  <Input type="date" value={intake.date_of_birth} onChange={(e) => handleIntakeChange('date_of_birth', e.target.value)} className="input-light" data-testid="input-dob" required />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">
                  Address
                </h3>
                <div className="space-y-2">
                  <Label className="text-slate-600">Street Address *</Label>
                  <Input value={intake.street_address} onChange={(e) => handleIntakeChange('street_address', e.target.value)} className="input-light" data-testid="input-street" required />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">City *</Label>
                    <Input value={intake.city} onChange={(e) => handleIntakeChange('city', e.target.value)} className="input-light" data-testid="input-city" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">State *</Label>
                    <Select onValueChange={(v) => handleIntakeChange('state', v)} value={intake.state}>
                      <SelectTrigger className="input-light" data-testid="intake-state-trigger"><SelectValue placeholder="Select state" /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200 max-h-72">
                        {Object.entries(US_STATE_CODE_TO_NAME).map(([code, name]) => (
                          <SelectItem key={code} value={name}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">ZIP Code *</Label>
                    <Input value={intake.zip_code} onChange={(e) => handleIntakeChange('zip_code', e.target.value)} className="input-light" data-testid="input-zip" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">County *</Label>
                    <Input value={intake.county} onChange={(e) => handleIntakeChange('county', e.target.value)} className="input-light" data-testid="input-county" required />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">
                  Emergency Contact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Contact Name *</Label>
                    <Input value={intake.emergency_contact_name} onChange={(e) => handleIntakeChange('emergency_contact_name', e.target.value)} className="input-light" data-testid="input-ec-name" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Relationship *</Label>
                    <Input value={intake.emergency_contact_relation} onChange={(e) => handleIntakeChange('emergency_contact_relation', e.target.value)} className="input-light" placeholder="e.g., Parent, Sibling, Friend" data-testid="input-ec-relation" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Contact Phone *</Label>
                    <Input type="tel" value={intake.emergency_contact_phone} onChange={(e) => handleIntakeChange('emergency_contact_phone', e.target.value)} className="input-light" data-testid="input-ec-phone" required />
                  </div>
                </div>
              </div>

              {/* Referral Source */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">
                  Referral Source
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">How did you hear about us? *</Label>
                    <Select onValueChange={(value) => handleIntakeChange('how_heard', value)} value={intake.how_heard}>
                      <SelectTrigger className="input-light" data-testid="select-how-heard">
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="search">Online Search</SelectItem>
                        <SelectItem value="social">Social Media</SelectItem>
                        <SelectItem value="referral">Friend/Family Referral</SelectItem>
                        <SelectItem value="drs">Division of Rehabilitation Services</SelectItem>
                        <SelectItem value="organization">Disability Organization</SelectItem>
                        <SelectItem value="haape">HAAPE</SelectItem>
                        <SelectItem value="event">Event or Conference</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">
                      Referral Source {intake.how_heard === "referral" && <span className="text-red-500">*</span>}
                    </Label>
                    <Input value={intake.referral_source} onChange={(e) => handleIntakeChange('referral_source', e.target.value)} className="input-light" placeholder="Name of person or organization" data-testid="input-referral-source" />
                  </div>
                </div>
              </div>

              {/* Resume / CV — required */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">
                  Resume / CV <span className="text-red-500">*</span>
                </h3>
                <div className="rounded-xl border-2 border-dashed border-[#1E3A8A]/30 bg-[#1E3A8A]/5 p-5">
                  <p className="text-sm text-slate-700 mb-3 leading-relaxed">
                    Please attach your resume — <strong>even a simple, one-page or minimal resume is wonderful!</strong> ✨
                    We're not looking for anything polished. We just want a starting point to understand
                    where you're at so we can meet you there. Don't have one yet? A short list of any jobs,
                    volunteering, schooling, or skills works perfectly.
                  </p>
                  <input
                    id="resume-upload"
                    type="file"
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                    className="hidden"
                    data-testid="input-resume"
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <label htmlFor="resume-upload" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium cursor-pointer hover:bg-[#152C6E] transition" data-testid="resume-upload-btn">
                      <Upload className="w-4 h-4" /> {resumeFile ? "Change file" : "Choose file"}
                    </label>
                    {resumeFile ? (
                      <span className="inline-flex items-center gap-2 text-sm text-green-700 font-medium" data-testid="resume-selected">
                        <Paperclip className="w-4 h-4" /> {resumeFile.name}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400">No file chosen yet (PDF, Word, or image)</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-4">
                {intakeRemaining > 0 ? (
                  <p className="text-sm text-amber-600 flex items-center gap-1.5" data-testid="intake-progress-hint">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                    {intakeRemaining} required {intakeRemaining === 1 ? "field" : "fields"} left to complete
                  </p>
                ) : (
                  <p className="text-sm text-green-600 flex items-center gap-1.5" data-testid="intake-progress-hint">
                    <CheckCircle className="w-4 h-4" /> All set — ready to continue!
                  </p>
                )}
                <Button 
                  onClick={() => submitStep(1)}
                  disabled={submitting || !intakeComplete}
                  className="btn-primary flex items-center gap-2 sm:ml-auto"
                  data-testid="next-step-1"
                >
                  {submitting ? (
                    <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Saving...</>
                  ) : (
                    <>Continue to Eligibility <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          </>
        )}

        {/* Account Gate — guest must create a login right after the intake form */}
        {showAccountGate && isGuest && (
          <Card className="card-light overflow-hidden" data-testid="account-gate">
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#152C6E] p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Lock className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-bold">Create your account to continue</h2>
                  <p className="text-white/70 text-sm">Set a password now to save your progress and finish your application.</p>
                </div>
              </div>
            </div>
            <CardContent className="pt-6 space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-green-800 text-sm">Your intake form is saved!</h3>
                  <p className="text-green-700 text-sm mt-1">Create a quick account so we can keep your information secure and pick up right where you left off.</p>
                </div>
              </div>

              {accountError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="account-gate-error">
                  {accountError}
                </div>
              )}

              <form onSubmit={handleEarlyAccountCreation} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-600">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      value={intake.email || ""}
                      disabled
                      className="input-light pl-10 bg-slate-50 text-slate-500"
                      data-testid="account-gate-email"
                    />
                  </div>
                  <p className="text-xs text-slate-400">This is the email you provided in your intake form.</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Create Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="password"
                      value={guestPassword}
                      onChange={(e) => setGuestPassword(e.target.value)}
                      className="input-light pl-10"
                      placeholder="Min. 6 characters"
                      required
                      minLength={6}
                      data-testid="account-gate-password"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Confirm Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="password"
                      value={guestConfirmPassword}
                      onChange={(e) => setGuestConfirmPassword(e.target.value)}
                      className="input-light pl-10"
                      placeholder="Confirm your password"
                      required
                      minLength={6}
                      data-testid="account-gate-confirm-password"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap justify-between items-center gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => { setShowAccountGate(false); setAccountError(""); }}
                    className="btn-outline flex items-center gap-2"
                    type="button"
                    data-testid="account-gate-back-btn"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to intake
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting || !guestPassword || guestPassword.length < 6}
                    className="btn-primary flex items-center gap-2"
                    data-testid="account-gate-submit-btn"
                  >
                    {submitting ? (
                      <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Creating Account...</>
                    ) : (
                      <>Create Account & Continue <ArrowRight className="w-4 h-4" /></>
                    )}
                  </Button>
                </div>
              </form>

              <div className="border-t border-slate-100 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => { setSaveEmail(intake.email || ""); setSaveDialogOpen(true); }}
                  className="text-[#1E3A8A] hover:underline font-medium"
                  data-testid="account-gate-save-later-btn"
                >
                  Not ready? Save &amp; continue later
                </button>
                <span className="text-slate-500">
                  Already have an account?{" "}
                  <a href="/login" className="text-[#1E3A8A] hover:underline font-medium">Sign in</a>
                </span>
              </div>
            </CardContent>
          </Card>
        )}


        {/* State Routing Interstitial — appears after Step 1 submit, before Step 2 */}
        {showStateRouting && (
          <StateRoutingScreen
            intake={intake}
            programs={programs}
            onContinueToEligibility={() => { setShowStateRouting(false); setCurrentStep(2); }}
            onBackToIntake={() => { setShowStateRouting(false); setCurrentStep(1); }}
          />
        )}

        {/* Step 2: Eligibility */}
        {currentStep === 2 && !showStateRouting && (
          <>
          <div className="relative rounded-xl overflow-hidden h-36" data-testid="eligibility-banner">
            <img src="https://aspiritech.org/wp-content/uploads/2023/10/DSC_1135-1024x683.jpeg" alt="Aspiritech Training" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F1B3D]/75 to-transparent flex items-center px-8">
              <div>
                <p className="text-[#1E3A8A] text-sm font-semibold uppercase tracking-wider mb-1">Step 2 of {totalSteps}</p>
                <h2 className="text-white text-lg sm:text-xl font-heading font-bold">Eligibility & Funding Information</h2>
              </div>
            </div>
          </div>
          <Card className="card-light" data-testid="step-2-eligibility">
            <CardHeader>
              <CardTitle className="text-[#0F1B3D] flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-[#1E3A8A]" />
                Step 2: Eligibility Information
              </CardTitle>
              <CardDescription className="text-slate-500">
                This information helps determine your eligibility for WIOA funding and support services
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Work Authorization */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">Work Authorization</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Citizenship Status *</Label>
                    <Select onValueChange={(value) => handleEligibilityChange('citizenship_status', value)} value={eligibility.citizenship_status}>
                      <SelectTrigger className="input-light" data-testid="select-citizenship"><SelectValue placeholder="Select status" /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="us_citizen">US Citizen</SelectItem>
                        <SelectItem value="permanent_resident">Permanent Resident</SelectItem>
                        <SelectItem value="authorized_alien">Authorized Alien</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Employment Status */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">Employment Status</h3>
                <div className="space-y-2">
                  <Label className="text-slate-600">Current Employment Status *</Label>
                  <Select onValueChange={(value) => handleEligibilityChange('employment_status', value)} value={eligibility.employment_status}>
                    <SelectTrigger className="input-light" data-testid="select-employment"><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="employed">Employed</SelectItem>
                      <SelectItem value="unemployed">Unemployed</SelectItem>
                      <SelectItem value="underemployed">Underemployed</SelectItem>
                      <SelectItem value="dislocated_worker">Dislocated Worker (laid off)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(eligibility.employment_status === 'employed' || eligibility.employment_status === 'underemployed') && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="space-y-2">
                      <Label className="text-slate-600">Employer Name <span className="text-red-500">*</span></Label>
                      <Input value={eligibility.employer_name} onChange={(e) => handleEligibilityChange('employer_name', e.target.value)} className="input-light" data-testid="input-employer-name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600">Job Title <span className="text-red-500">*</span></Label>
                      <Input value={eligibility.job_title} onChange={(e) => handleEligibilityChange('job_title', e.target.value)} className="input-light" data-testid="input-job-title" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600">Hours per Week <span className="text-red-500">*</span></Label>
                      <Input type="number" value={eligibility.hours_per_week} onChange={(e) => handleEligibilityChange('hours_per_week', e.target.value)} className="input-light" data-testid="input-hours-week" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600">Hourly Wage ($) <span className="text-red-500">*</span></Label>
                      <Input type="number" step="0.01" value={eligibility.hourly_wage} onChange={(e) => handleEligibilityChange('hourly_wage', e.target.value)} className="input-light" data-testid="input-hourly-wage" />
                    </div>
                  </div>
                )}

                {eligibility.employment_status === 'dislocated_worker' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="space-y-2">
                      <Label className="text-slate-600">Previous Employer <span className="text-red-500">*</span></Label>
                      <Input value={eligibility.previous_employer} onChange={(e) => handleEligibilityChange('previous_employer', e.target.value)} className="input-light" data-testid="input-previous-employer" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600">Separation Date <span className="text-red-500">*</span></Label>
                      <Input type="date" value={eligibility.separation_date} onChange={(e) => handleEligibilityChange('separation_date', e.target.value)} className="input-light" data-testid="input-separation-date" />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label className="text-slate-600">Reason for Layoff <span className="text-red-500">*</span></Label>
                      <Input value={eligibility.layoff_reason} onChange={(e) => handleEligibilityChange('layoff_reason', e.target.value)} className="input-light" data-testid="input-layoff-reason" />
                    </div>
                  </div>
                )}
              </div>

              {/* Public Assistance */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">Public Assistance</h3>
                <p className="text-sm text-slate-500">Check all that apply:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { id: 'receives_snap', label: 'SNAP (Food Stamps)' },
                    { id: 'receives_tanf', label: 'TANF' },
                    { id: 'receives_ssi', label: 'SSI' },
                    { id: 'receives_ssdi', label: 'SSDI' },
                    { id: 'receives_other_assistance', label: 'Other Assistance' }
                  ].map(item => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Checkbox id={item.id} checked={eligibility[item.id]} onCheckedChange={(checked) => handleEligibilityChange(item.id, checked)} className="border-white/30 data-[state=checked]:bg-[#FFD507] data-[state=checked]:border-[#FFD507]" />
                      <Label htmlFor={item.id} className="text-slate-600 text-sm">{item.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Eligibility */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">Additional Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { id: 'is_veteran', label: 'Veteran' },
                    { id: 'is_veteran_spouse', label: 'Veteran Spouse' },
                    { id: 'is_foster_care', label: 'Current/Former Foster Youth' },
                    { id: 'is_homeless', label: 'Homeless/Housing Insecure' },
                    { id: 'has_disability', label: 'Individual with a Disability' }
                  ].map(item => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Checkbox id={item.id} checked={eligibility[item.id]} onCheckedChange={(checked) => handleEligibilityChange(item.id, checked)} className="border-white/30 data-[state=checked]:bg-[#FFD507] data-[state=checked]:border-[#FFD507]" />
                      <Label htmlFor={item.id} className="text-slate-600 text-sm">{item.label}</Label>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-600">Barriers to Employment <span className="text-red-500">*</span></Label>
                  <Textarea value={eligibility.barriers_to_employment} onChange={(e) => handleEligibilityChange('barriers_to_employment', e.target.value)} className="input-base min-h-[80px]" placeholder="List any barriers you face in finding or maintaining employment (type 'None' if none apply)..." data-testid="input-barriers" />
                </div>
              </div>

              {/* Demographics */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">Demographics (Federal Reporting)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Sex / Gender *</Label>
                    <Select onValueChange={(value) => handleEligibilityChange('gender', value)} value={eligibility.gender}>
                      <SelectTrigger className="input-light" data-testid="select-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="non_binary">Non-Binary</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Ethnicity (Hispanic/Latino) — separate, prominent */}
              <div className="space-y-3">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">Ethnicity</h3>
                <p className="text-sm text-slate-500">
                  The U.S. Census separates ethnicity from race. Please answer both questions.
                </p>
                <fieldset className="rounded-xl border-2 border-slate-200 bg-slate-50 p-4" data-testid="ethnicity-fieldset">
                  <legend className="px-2 text-sm font-semibold text-slate-700">Are you Hispanic or Latino?</legend>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                    {[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                      { value: null, label: "Prefer not to say" },
                    ].map(opt => {
                      const checked = eligibility.is_hispanic_latino === opt.value;
                      return (
                        <label
                          key={String(opt.value)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition ${
                            checked
                              ? "border-[#FFD507] bg-white ring-2 ring-[#FFD507]/30"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                          data-testid={`hispanic-latino-${opt.label.toLowerCase().replace(/\s/g,'-')}`}
                        >
                          <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${checked ? "border-[#FFD507] bg-[#FFD507]" : "border-slate-300"}`}>
                            {checked && <span className="w-2 h-2 rounded-full bg-[#0F1B3D]" />}
                          </span>
                          <span className="text-sm font-medium text-slate-800">{opt.label}</span>
                          <input
                            type="radio"
                            name="is_hispanic_latino"
                            className="sr-only"
                            checked={checked}
                            onChange={() => handleEligibilityChange('is_hispanic_latino', opt.value)}
                          />
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              </div>

              {/* Race (separate from ethnicity) */}
              <div className="space-y-3">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">Race <span className="text-red-500">*</span></h3>
                <p className="text-sm text-slate-500">Select all that apply. Race is reported separately from ethnicity.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { value: 'white', label: 'White' },
                    { value: 'black', label: 'Black / African American' },
                    { value: 'asian', label: 'Asian' },
                    { value: 'american_indian', label: 'American Indian / Alaska Native' },
                    { value: 'pacific_islander', label: 'Native Hawaiian / Pacific Islander' },
                    { value: 'prefer_not_to_answer', label: 'Prefer not to answer' }
                  ].map(race => {
                    const checked = eligibility.race.includes(race.value);
                    return (
                      <label
                        key={race.value}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition ${
                          checked
                            ? "border-[#FFD507] bg-white ring-2 ring-[#FFD507]/20"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                        data-testid={`race-${race.value}`}
                      >
                        <Checkbox
                          id={`race_${race.value}`}
                          checked={checked}
                          onCheckedChange={(c) => handleRaceChange(race.value, c)}
                          className="data-[state=checked]:bg-[#FFD507] data-[state=checked]:border-[#FFD507]"
                        />
                        <span className="text-sm font-medium text-slate-800">{race.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* English Language Proficiency */}
              <div className="space-y-3">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">English Language</h3>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_english_primary"
                    checked={eligibility.is_english_primary}
                    onCheckedChange={(c) => handleEligibilityChange('is_english_primary', c)}
                    className="data-[state=checked]:bg-[#FFD507] data-[state=checked]:border-[#FFD507]"
                  />
                  <Label htmlFor="is_english_primary" className="text-slate-700">English is my primary language</Label>
                </div>
                {!eligibility.is_english_primary && (
                  <div className="ml-7 flex items-center gap-2" data-testid="lep-followup">
                    <Checkbox
                      id="limited_english_proficiency"
                      checked={eligibility.limited_english_proficiency}
                      onCheckedChange={(c) => handleEligibilityChange('limited_english_proficiency', c)}
                      className="data-[state=checked]:bg-[#FFD507] data-[state=checked]:border-[#FFD507]"
                    />
                    <Label htmlFor="limited_english_proficiency" className="text-slate-700 text-sm">
                      I have limited English proficiency (may need translated materials or interpreter support)
                    </Label>
                  </div>
                )}
              </div>

              {/* Education & Skills */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">Education &amp; Skills</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Highest Education Completed *</Label>
                    <Select onValueChange={(value) => handleEligibilityChange('highest_education', value)} value={eligibility.highest_education}>
                      <SelectTrigger className="input-light" data-testid="select-education"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="no_hs">No High School Diploma</SelectItem>
                        <SelectItem value="hs_diploma">High School Diploma</SelectItem>
                        <SelectItem value="ged">GED</SelectItem>
                        <SelectItem value="some_college">Some College</SelectItem>
                        <SelectItem value="associates">Associate's Degree</SelectItem>
                        <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
                        <SelectItem value="masters">Master's Degree</SelectItem>
                        <SelectItem value="doctorate">Doctorate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">School Status *</Label>
                    <Select
                      onValueChange={(value) => {
                        handleEligibilityChange('school_status', value);
                        handleEligibilityChange('is_currently_in_school', value === 'in_school');
                      }}
                      value={eligibility.school_status}
                    >
                      <SelectTrigger className="input-light" data-testid="school-status-trigger"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="in_school">In school</SelectItem>
                        <SelectItem value="out_of_school">Out of school</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Prior IT Certifications</Label>
                  <Input value={eligibility.prior_certifications} onChange={(e) => handleEligibilityChange('prior_certifications', e.target.value)} className="input-light" placeholder="e.g., CompTIA A+, Google IT Support Certificate" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="basic_skills_deficiency_tested"
                      checked={eligibility.basic_skills_deficiency_tested}
                      onCheckedChange={(c) => handleEligibilityChange('basic_skills_deficiency_tested', c)}
                      className="data-[state=checked]:bg-[#FFD507] data-[state=checked]:border-[#FFD507]"
                    />
                    <Label htmlFor="basic_skills_deficiency_tested" className="text-slate-700 text-sm">
                      I have taken a basic skills test (TABE or CASAS)
                    </Label>
                  </div>
                  {eligibility.basic_skills_deficiency_tested && (
                    <Textarea
                      value={eligibility.basic_skills_results}
                      onChange={(e) => handleEligibilityChange('basic_skills_results', e.target.value)}
                      className="input-base min-h-[60px] ml-7"
                      placeholder="Test type (TABE / CASAS) and your literacy / numeracy levels, if known"
                      data-testid="basic-skills-results"
                    />
                  )}
                  <p className="text-xs text-slate-500 ml-7">
                    Some local workforce boards require this — leave blank if you haven't taken it yet.
                  </p>
                </div>
              </div>

              {/* Accommodations */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2">Accommodations</h3>
                <div className="space-y-2">
                  <Label className="text-slate-600">What accommodations might you need? <span className="text-red-500">*</span></Label>
                  <Textarea value={eligibility.accommodations_needed} onChange={(e) => handleEligibilityChange('accommodations_needed', e.target.value)} className="input-base min-h-[100px]" placeholder="Describe any accommodations that would help you succeed (type 'None' if none needed)..." data-testid="input-accommodations" />
                </div>
              </div>

              {/* Navigation */}
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-4">
                <Button variant="outline" onClick={() => setCurrentStep(1)} className="btn-outline flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" /> Back
                </Button>
                {eligibilityRemaining > 0 ? (
                  <p className="text-sm text-amber-600 flex items-center gap-1.5 sm:ml-auto sm:mr-4" data-testid="eligibility-progress-hint">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                    {eligibilityRemaining} required {eligibilityRemaining === 1 ? "field" : "fields"} left to complete
                  </p>
                ) : (
                  <p className="text-sm text-green-600 flex items-center gap-1.5 sm:ml-auto sm:mr-4" data-testid="eligibility-progress-hint">
                    <CheckCircle className="w-4 h-4" /> All set — ready to continue!
                  </p>
                )}
                <Button 
                  onClick={() => submitStep(2)}
                  disabled={submitting || !eligibilityComplete}
                  className="btn-primary flex items-center gap-2"
                  data-testid="next-step-2"
                >
                  {submitting ? (
                    <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Saving...</>
                  ) : (
                    <>Continue to Application <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          </>
        )}

        {/* Step 3: Program Readiness — pick ONE program of interest + answer readiness questions */}
        {currentStep === 3 && (
          <>
          {!selectedProgramId && (
            <Card className="card-light overflow-hidden">
              <div className="bg-gradient-to-r from-[#1E3A8A] to-[#152C6E] p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <GraduationCap className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-heading font-bold">Step 3: Program Readiness</h2>
                    <p className="text-white/70 text-sm">Choose the program you're most interested in, then answer a few readiness questions.</p>
                  </div>
                </div>
              </div>
              <CardContent className="pt-6 space-y-4">
                {/* Capacity disclaimer */}
                <div className="rounded-xl border-2 border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="capacity-disclaimer">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900">
                    Due to overwhelming demand and capacity restraints, we ask participants to
                    select <strong>one program</strong> that they are most interested in. You may
                    optionally name a second-choice backup. Choosing a program here expresses
                    your interest — <strong>an instructor or admin will review and confirm your
                    enrollment</strong> based on capacity.
                  </p>
                </div>

                <Label className="text-slate-700 font-semibold">Program of Interest *</Label>
                <div className="grid gap-3">
                  {programs.filter(p => p.is_active).map((prog) => (
                    <button
                      key={prog.program_id}
                      onClick={() => {
                        setSelectedProgramId(prog.program_id);
                        setSelectedCohortId("");
                        // If user previously picked this same prog as backup, clear it
                        if (programApp.backup_program_id === prog.program_id) {
                          setProgramApp((p) => ({ ...p, backup_program_id: "" }));
                        }
                      }}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        selectedProgramId === prog.program_id
                          ? "border-brand-gold bg-brand-gold/5"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                      data-testid={`select-program-${prog.program_id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center flex-shrink-0 ${
                          selectedProgramId === prog.program_id ? "border-brand-gold bg-brand-gold" : "border-slate-300"
                        }`}>
                          {selectedProgramId === prog.program_id && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{prog.name}</h3>
                          <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                            {prog.duration_weeks && <span>{prog.duration_weeks} weeks</span>}
                            {prog.cohorts?.length > 0 ? (
                              <span>{prog.cohorts.length} start date{prog.cohorts.length !== 1 ? "s" : ""} available</span>
                            ) : (
                              <span className="text-amber-600">Coming Soon</span>
                            )}
                          </div>
                          {prog.certifications?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {prog.certifications.map((c, i) => (
                                <span key={i} className="text-xs bg-brand-gold/10 text-brand-navy px-2 py-0.5 rounded-full">{c}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Optional backup program — only after a primary is picked */}
                {selectedProgramId && (
                  <div className="space-y-2 pt-2" data-testid="backup-program-section">
                    <Label className="text-slate-600 text-sm">
                      Optional: backup program (second choice if your first is full)
                    </Label>
                    <Select
                      onValueChange={(v) => setProgramApp((p) => ({ ...p, backup_program_id: v === "_none" ? "" : v }))}
                      value={programApp.backup_program_id || "_none"}
                    >
                      <SelectTrigger className="input-light" data-testid="backup-program-trigger">
                        <SelectValue placeholder="No second choice" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="_none">No second choice</SelectItem>
                        {programs.filter(p => p.is_active && p.program_id !== selectedProgramId).map(p => (
                          <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Backup is only considered if your primary program is at capacity. You will not
                      be enrolled in both — final assignment is made by an admin or instructor.
                    </p>
                  </div>
                )}

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setCurrentStep(2)} className="btn-outline flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {selectedProgramId && (
            <ProgramApplicationStep
              programId={selectedProgramId}
              programName={selectedProgram?.name || ""}
              onSubmit={handleProgramApplicationSubmit}
              onBack={() => setSelectedProgramId("")}
              submitting={submitting}
              existingData={enrollment?.program_application}
            />
          )}
          </>
        )}

        {/* Step 4: Choose Your Start Date */}
        {currentStep === 4 && (() => {
          const ps = isGuest 
            ? { program_id: selectedProgramId, program_name: programs.find(p => p.program_id === selectedProgramId)?.name || "" }
            : (enrollment?.program_selection || {});
          const prog = programs.find(p => p.program_id === (ps.program_id || selectedProgramId)) || {};
          const cohorts = prog.cohorts || [];
          const openCohorts = cohorts.filter(c => !c.max_participants || c.enrolled_count < c.max_participants);
          const allFull = cohorts.length > 0 && openCohorts.length === 0;
          const noCohortsAvailable = cohorts.length === 0;

          return (
            <Card className="card-light overflow-hidden">
              <div className="bg-gradient-to-r from-[#1E3A8A] to-[#152C6E] p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <CalendarDays className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-heading font-bold">Step 4: Preferred Start Date</h2>
                    <p className="text-white/70 text-sm">{ps.program_name || prog.name} &mdash; pick the date you'd prefer. An instructor or admin will confirm your enrollment based on capacity.</p>
                  </div>
                </div>
              </div>
              <CardContent className="pt-6 space-y-6">
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="admin-assignment-note">
                  <strong>Heads up:</strong> Choosing a date here saves your preference. Your final cohort
                  assignment is confirmed by an Aspiritech instructor or admin so we can balance group
                  composition and capacity. We'll email you once it's confirmed.
                </div>
                {noCohortsAvailable && (
                  <div className="text-center py-8 space-y-4">
                    <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto">
                      <CalendarDays className="w-8 h-8 text-amber-500" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">No Dates Scheduled Yet</h3>
                      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                        This program doesn't have any upcoming start dates scheduled yet. Add yourself to the waitlist and we'll notify you when new dates are available.
                      </p>
                    </div>
                    <Button onClick={() => handleCohortSelection(null, true)} disabled={submitting} className="btn-primary text-base px-8 py-3" data-testid="join-waitlist-btn">
                      {submitting ? <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" /> Adding...</> : <><Clock className="w-4 h-4 mr-2" /> Add me to the waitlist</>}
                    </Button>
                  </div>
                )}

                {allFull && !noCohortsAvailable && (
                  <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
                      <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <h3 className="text-lg font-semibold text-amber-800">All Cohorts Are Currently Full</h3>
                      <p className="text-sm text-amber-700 mt-1">We will be in touch as we are able to add additional cohorts.</p>
                    </div>
                    <div className="space-y-2">
                      {cohorts.map((cohort) => (
                        <div key={cohort.cohort_id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg bg-slate-50 opacity-60">
                          <div>
                            <p className="text-sm font-medium text-slate-700">{formatCohortDate(cohort.start_date)}</p>
                            <p className="text-xs text-slate-400">{cohort.instructors?.join(", ")}</p>
                          </div>
                          <span className="text-xs font-semibold text-red-500 bg-red-50 px-3 py-1 rounded-full border border-red-200">Full</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-center">
                      <Button onClick={() => handleCohortSelection(null, true)} disabled={submitting} className="btn-primary text-base px-8 py-3" data-testid="join-waitlist-btn">
                        {submitting ? <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" /> Adding...</> : <><Clock className="w-4 h-4 mr-2" /> Add me to the waitlist</>}
                      </Button>
                    </div>
                  </div>
                )}

                {!noCohortsAvailable && !allFull && (
                  <div className="space-y-4">
                    {/* Fee notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="fee-notice">
                      <div className="flex items-start gap-3">
                        <CreditCard className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-amber-800 text-sm">$99 Application & Reservation Fee</h4>
                          <p className="text-sm text-amber-700 mt-1">A <strong>$99 non-refundable reservation fee</strong> per program is required to hold your seat.</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-amber-600 flex items-center gap-1.5">
                              <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
                              <strong>Refundable</strong> if we are unable to accommodate your request to join the program.
                            </p>
                            <p className="text-xs text-amber-600 flex items-center gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                              <strong>Non-refundable</strong> if you are accepted and do not attend.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Label className="text-slate-700 font-semibold">Available Start Dates</Label>
                    <div className="grid gap-3">
                      {cohorts.map((cohort) => {
                        const spotsLeft = (cohort.max_participants || 0) - (cohort.enrolled_count || 0);
                        const isFull = cohort.max_participants > 0 && spotsLeft <= 0;
                        return (
                          <div key={cohort.cohort_id} className={`p-4 rounded-xl border-2 ${isFull ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`} data-testid={`cohort-option-${cohort.cohort_id}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className={`font-semibold ${isFull ? "text-slate-400" : "text-slate-900"}`}>{formatCohortDate(cohort.start_date)}</p>
                                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-500">
                                  {cohort.schedule && <span>{cohort.schedule}</span>}
                                  {cohort.instructors?.length > 0 && <span>Instructors: {cohort.instructors.join(", ")}</span>}
                                  {!isFull && <span className="text-[#059669]">{spotsLeft} spot{spotsLeft !== 1 ? "s" : ""} remaining</span>}
                                </div>
                              </div>
                              {isFull ? (
                                <span className="text-xs font-semibold text-red-500 bg-red-50 px-4 py-2 rounded-lg border border-red-200" data-testid={`cohort-full-${cohort.cohort_id}`}>Full</span>
                              ) : (
                                <Button onClick={() => handleCohortSelection(cohort.cohort_id, false)} disabled={submitting} className="btn-accent px-4 flex items-center gap-1.5" data-testid={`apply-cohort-${cohort.cohort_id}`}>
                                  {submitting ? "Processing..." : <><CreditCard className="w-4 h-4" /> Pay $99 &amp; Reserve</>}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex justify-start pt-4">
                  <Button variant="outline" onClick={() => setCurrentStep(3)} className="btn-outline flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Step 5: Create Account (Guest Only) */}
        {currentStep === 5 && isGuest && (
          <Card className="card-light overflow-hidden" data-testid="step-5-create-account">
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#152C6E] p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Lock className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-bold">Almost Done! Create Your Account</h2>
                  <p className="text-white/70 text-sm">Set a password to save your application and track your enrollment status.</p>
                </div>
              </div>
            </div>
            <CardContent className="pt-6 space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-green-800 text-sm">Your application is ready to submit!</h3>
                  <p className="text-green-700 text-sm mt-1">Create a password below to save your enrollment and get access to your dashboard.</p>
                </div>
              </div>

              {paymentVerified && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3" data-testid="payment-confirmed-badge">
                  <CreditCard className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-800 text-sm">$99 Reservation Fee Paid</h3>
                    <p className="text-blue-700 text-sm mt-1">Your seat is being held. Complete your account to finalize enrollment.</p>
                  </div>
                </div>
              )}

              {accountError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="account-creation-error">
                  {accountError}
                </div>
              )}

              <form onSubmit={handleGuestAccountCreation} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-600">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      value={intake.email || (() => { try { return JSON.parse(localStorage.getItem("aspiritech_guest_intake") || "{}").email || ""; } catch { return ""; } })()}
                      disabled
                      className="input-light pl-10 bg-slate-50 text-slate-500"
                      data-testid="account-email"
                    />
                  </div>
                  <p className="text-xs text-slate-400">This is the email you provided in your intake form.</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Create Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="password"
                      value={guestPassword}
                      onChange={(e) => setGuestPassword(e.target.value)}
                      className="input-light pl-10"
                      placeholder="Min. 6 characters"
                      required
                      minLength={6}
                      data-testid="account-password"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Confirm Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="password"
                      value={guestConfirmPassword}
                      onChange={(e) => setGuestConfirmPassword(e.target.value)}
                      className="input-light pl-10"
                      placeholder="Confirm your password"
                      required
                      minLength={6}
                      data-testid="account-confirm-password"
                    />
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setCurrentStep(4)} className="btn-outline flex items-center gap-2" type="button">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting || !guestPassword || guestPassword.length < 6}
                    className="btn-primary flex items-center gap-2"
                    data-testid="submit-enrollment-btn"
                  >
                    {submitting ? (
                      <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Creating Account...</>
                    ) : (
                      <>Create Account & Submit <ArrowRight className="w-4 h-4" /></>
                    )}
                  </Button>
                </div>
              </form>

              <div className="text-center text-sm text-slate-500 border-t border-slate-100 pt-4">
                Already have an account?{" "}
                <a href="/login" className="text-[#1E3A8A] hover:underline font-medium">Sign in here</a>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Save & continue later modal */}
      <Dialog open={saveDialogOpen} onOpenChange={(o) => { setSaveDialogOpen(o); if (!o) setSaved(null); }}>
        <DialogContent className="max-w-md bg-white border-slate-200" data-testid="save-draft-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Save className="w-5 h-5 text-[#1E3A8A]" />
              Save & continue later
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              We'll email you a one-click link to pick up where you left off — works on any device for 14 days. No account needed yet.
            </DialogDescription>
          </DialogHeader>

          {!saved ? (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-slate-700 text-sm">Email address</Label>
                <Input
                  type="email"
                  value={saveEmail}
                  onChange={(e) => setSaveEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-base mt-1"
                  data-testid="save-draft-email-input"
                />
                <p className="text-xs text-slate-400 mt-1">We'll only use this to send your resume link. No marketing emails.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="save-draft-success">
                <p className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Saved! Check your inbox.
                </p>
                <p className="text-xs text-emerald-700 mt-1">
                  We sent a resume link to <strong>{saveEmail || intake.email}</strong>. Click it on any device to keep going.
                </p>
              </div>
              {saved.mocked_email && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-amber-800 mb-1">📝 Email is mocked in this environment.</p>
                  <p className="text-xs text-amber-700 mb-2">Copy this link to test the resume flow directly:</p>
                  <Input readOnly value={saved.resume_url} className="text-[10px] font-mono bg-white" data-testid="save-draft-resume-url" />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!saved ? (
              <>
                <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveDraft} disabled={saving} className="btn-primary" data-testid="save-draft-confirm-btn">
                  {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending</> : <><Mail className="w-4 h-4 mr-1.5" /> Send my resume link</>}
                </Button>
              </>
            ) : (
              <Button onClick={() => setSaveDialogOpen(false)} className="btn-primary" data-testid="save-draft-done-btn">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </LightPageLayout>
  );
};

export default EnrollmentWizard;
